import { useCallback, useMemo, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import {
	setAccessToken,
	setAccessTokenExpiryTime,
	setAccountRealms,
} from '../actions/authActions';
import { getRefreshTokenApi, isAuthApi } from '../Login/apiLogin';
import { processTokenResponse, getHomePageUrl } from '../utils';
import { store, userLogout } from '../store';
import { LOCAL_STORAGE_KEYS } from '../constants/constants';

const useRequestor = () => {
	const dispatch = useDispatch();

	const activeRealm = useSelector((state) =>
		state.getIn(['auth', 'activeRealm']),
	);

	const customersActiveRealm = useSelector((state) =>
		state.getIn(['auth', 'customersActiveRealm']),
	);

	const accountRealms = useSelector((state) =>
		state.getIn(['auth', 'accountRealms']),
	);

	const sortedRealms = useMemo(() => {
		if (!accountRealms) return [];

		return [...accountRealms].sort((a, b) =>
			a.SecurityAuthenticationRealmName?.localeCompare(
				b.SecurityAuthenticationRealmName,
			),
		);
	}, [accountRealms]);

	const accountRealmsOptions = useMemo(
		() =>
			sortedRealms
				.filter((r) => r.SecurityAuthenticationRealmName != null)
				.map((r) => ({
					name: r.SecurityAuthenticationAccountGroupLabel,
					value: r.SecurityAuthenticationRealmName,
				})),
		[sortedRealms],
	);

	const isAuthError = useCallback((error) => {
		const code = Number(error?.errorResponse?.code || error?.status);

		return code === 401 || code === 403;
	}, []);

	const handleLogout = useCallback(() => {
		store.dispatch(userLogout());
		window.location.href = getHomePageUrl();
		window.localStorage.clear();
	}, []);

	const refreshTokensAndRetry = useCallback(
		(realmName, onRetry, onFailure) => {
			const fail = onFailure || handleLogout;
			const targetRealmName =
				realmName || customersActiveRealm?.SecurityAuthenticationRealmName;

			getRefreshTokenApi(
				() => {
					// if SA refresh token api fail then we logout user as we cannot refresh token and continue session
					fail();
				},
				(response) => {
					const { newToken, newExpiryTime } = processTokenResponse(response);
					dispatch(setAccessToken(newToken));
					dispatch(setAccessTokenExpiryTime(newExpiryTime));

					isAuthApi(
						() => {
							fail();
						},
						(res) => {
							if (
								res?.successResponse?.data?.SecurityAuthenticated === 'true'
							) {
								const realms =
									res.successResponse.data.SecurityAuthenticationRealms;
								dispatch(setAccountRealms(realms));

								const refreshedRealm = realms?.find(
									(r) => r.SecurityAuthenticationRealmName === targetRealmName,
								);
								if (!refreshedRealm?.SecurityAuthenticationRealmToken) {
									fail();
									return;
								}
								localStorage.setItem(
									LOCAL_STORAGE_KEYS.customerAccountToken,
									refreshedRealm.SecurityAuthenticationRealmToken,
								);
								onRetry();
							} else {
								fail();
							}
						},
					);
				},
			);
		},
		[dispatch, handleLogout, customersActiveRealm],
	);

	const getCustomerAuthHeaders = useCallback(() => {
		const token = localStorage.getItem(LOCAL_STORAGE_KEYS.customerAccountToken);
		const tenantId =
			customersActiveRealm?.SecurityAuthenticationRealmName?.toLowerCase();
		if (!token || !tenantId) return null;
		return {
			'X-Tenant-Id': tenantId,
			Authorization: `Bearer ${token}`,
		};
	}, [customersActiveRealm]);

	const hasAttemptedRefreshRef = useRef(false);

	/**
	 * Wraps any API call with automatic 401/403 token refresh and retry.
	 * @param {object} options
	 * @param {function} options.apiCall - Function receiving (onError, onSuccess, refreshedHeaders).
	 *   `refreshedHeaders` is null on first attempt, populated on retry after token refresh.
	 *   Wire your API call inside, using refreshedHeaders when provided.
	 * @param {string|null} options.realmName - Realm name to match after refresh (null for current active realm).
	 * @param {function} options.onError - Handler for non-auth errors.
	 * @param {function} options.onSuccess - Handler for successful responses.
	 */
	const apiCallWithAuthRetry = useCallback(
		({ apiCall, realmName = null, onError, onSuccess }) => {
			const attempt = (refreshedHeaders) => {
				apiCall(
					(error) => {
						if (isAuthError(error) && !hasAttemptedRefreshRef.current) {
							hasAttemptedRefreshRef.current = true;
							refreshTokensAndRetry(realmName, () => {
								attempt(getCustomerAuthHeaders());
							});
							return;
						}
						hasAttemptedRefreshRef.current = false;
						onError(error);
					},
					(data) => {
						hasAttemptedRefreshRef.current = false;
						onSuccess(data);
					},
					refreshedHeaders,
				);
			};
			attempt(null);
		},
		[isAuthError, refreshTokensAndRetry, getCustomerAuthHeaders],
	);

	return {
		requestorId: activeRealm?.SecurityAuthenticationRequestorId,
		tenantId: activeRealm?.SecurityAuthenticationRealmName?.toLowerCase(),
		realmToken: activeRealm?.SecurityAuthenticationRealmToken,
		channels: activeRealm?.SecurityAuthenticationAccounts,
		accountRealms: sortedRealms,
		accountRealmsOptions,
		// Account search customer specific persistant realm config
		customersRequestorId:
			customersActiveRealm?.SecurityAuthenticationRequestorId,
		apiCallWithAuthRetry,
	};
};

export default useRequestor;
